import React from 'react';
import type { ElementType } from 'react';
import { Link } from '@remix-run/react';
import { Breadcrumb, useTheme, useStyles } from '@archipro-design/aria';

import * as S from './PageHeader.style';
import { superscriptize } from '~/utils';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';

interface PageHeaderProps {
    data: {
        Breadcrumbs: ProductPageLoaderData['product']['Breadcrumbs'];
        Title: ProductPageLoaderData['product']['Title'];
        Professional: Pick<
            ProductPageLoaderData['product']['Professional'],
            'Title' | 'Link'
        >;
    };
    isSticky?: boolean;
}

/**
 * The header of the product detail page.
 */
const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
    function PageHeader(
        { data: { Breadcrumbs, Title, Professional }, isSticky = false },
        ref,
    ) {
        const theme = useTheme();
        const breadcrumbColor = theme.siteVariables.colors.grey[300];
        const iconAdjustedTitle = superscriptize(Title);
        const { css } = useStyles({ isSticky });

        return (
            <div ref={ref} className={css(S.ProductHeaderContainer)}>
                <div className={css(S.PageHeader)}>
                    <div className="mt-auto flex justify-between pt-1.5 md:hidden">
                        {!isSticky && (
                            <Breadcrumb
                                logo={null}
                                home={{
                                    link: true,
                                    as: Link,
                                    label: 'Home',
                                    to: '/',
                                }}
                                items={Breadcrumbs.map((breadcrumb) => ({
                                    label: breadcrumb.label,
                                    href: breadcrumb.href,
                                    as: breadcrumb.as as ElementType,
                                    variables: {
                                        breadcrumbItemTextColor:
                                            breadcrumbColor,
                                    },
                                }))}
                                data-testid="breadcrumb-menu"
                                variables={{
                                    breadcrumbFontSize:
                                        theme.siteVariables.fontSizes.caption04,
                                }}
                            />
                        )}
                    </div>
                    <div className="flex w-full flex-col md:relative">
                        <h1
                            className="mb-0 mt-0 px-4.5 py-3 text-5 leading-[1.1] text-black md:my-8 md:px-0 md:py-0 md:text-15 md:leading-[1.1] md:tracking-[-1.8px]"
                            dangerouslySetInnerHTML={{
                                __html: iconAdjustedTitle,
                            }}
                        />
                        <Link
                            to={`${Professional.Link}/our-products`}
                            className="mb-0 mt-0 px-4.5 text-4 leading-[1.1] text-[#b6b6b6] hover:text-gray-400 md:absolute md:-bottom-[2px] md:left-0 md:px-0 md:text-[1.25rem] md:leading-[1.1] md:tracking-[-1.4px]"
                        >
                            {Professional.Title}
                        </Link>
                    </div>
                </div>
            </div>
        );
    },
);

export default PageHeader;
